import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CommonService } from 'src/app/services/common/common.service';
import { DataDslrService } from 'src/app/services/data-dslr/data-dslr.service';
import { AppRoute } from 'src/app/shared/constants/app-route.constant';
import { PHOTOBOOTH_MODAL_OPTIONS } from 'src/app/shared/constants/modal-options.constant';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { AppTheme, GlobalConfigKey, InterfaceErrorText, StorageKey } from 'src/app/shared/constants/global-config.constant';
import { OmegaRandom } from 'src/app/shared/constants/omega-random.constat';
import { IResponseBranchUserInterface } from 'src/app/shared/interfaces/interface';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';

/**
 * What the ready area shows:
 * - `loading`: a spinner, while the branch's picture downloads (kept invisible until it
 *   is complete - a slow connection used to draw it top to bottom, or not at all)
 * - `picture`: the branch's picture, faded in
 * - `builtin`: app-ready-fallback, when there is no picture or it did not arrive in time
 */
export type ReadyState = 'loading' | 'picture' | 'builtin';

/** Further attempts at the branch's picture after it fails, this far apart. */
export const READY_IMAGE_RETRIES = 2;
export const READY_IMAGE_RETRY_DELAY_MS = 1000;
/** A paid customer waits on this picture; past this, the built-in screen is shown. */
export const READY_IMAGE_TIMEOUT_MS = 5000;

@Component({
  selector: 'app-payment-success',
  templateUrl: './payment-success.component.html',
  styleUrls: ['./payment-success.component.scss']
})
export class PaymentSuccessComponent {

  constructor(
    private router: Router,
    private dataDslrService: DataDslrService,
    private activatedRoute: ActivatedRoute,
    public commonService: CommonService,
    private ngbModal: NgbModal,
    private themeService: ThemeService,
  ) { }

  retrying = false;
  skipping = false;
  branchInterface: IResponseBranchUserInterface;
  modalRef: NgbModalRef;

  private dslrInFlight = false;

  /**
   * The branch's own picture comes from a remote URL, and one that is slow or fails used
   * to leave a white screen with nothing telling a customer who has just paid to touch
   * it. So it gets a spinner, a short retry and a deadline, and then the built-in screen;
   * a picture that completes after that still cross-fades in.
   */
  readyState: ReadyState = 'builtin';
  /** The built-in screen has been shown - kept under a late picture so it cross-fades. */
  builtinShown = false;
  /** The branch picture's current request, or null when there is none to show. */
  readyImageSrc: string | null = null;
  theme: string = AppTheme.DEFAULT;
  readonly AppTheme = AppTheme;
  private readyImageUrl: string | null = null;
  private readyImageAttempt = 0;
  private readyImageLoaded = false;
  private readyImageRetryTimer: any = null;
  private readyImageDeadline: any = null;

  @ViewChild('pointCollectModal') pointCollectModal: any;

  ngOnInit() {

    this.branchInterface = JSON.parse(localStorage.getItem(StorageKey.BRANCH_INTERFACE));
    this.theme = this.themeService.getTheme();

    // Decided before the first render so the "touch to start" image never flashes on a
    // branch where nobody is meant to touch it. Requiring the dslrbooth config here is
    // what keeps an unconfigured kiosk from silently auto-navigating to /config.
    this.skipping = this.commonService.isSkipPaymentSuccessPageBranch()
      && this.dataDslrService.checkDslrBoothConfig();

    if (this.skipping) {
      // The DSLR call can take 15s+ with its retries — put the spinner up straight away.
      this.commonService.isLoading = true;
    }
    else {
      this.showReadyImage(this.branchInterface?.ready_background_image_url);
    }

    this.activatedRoute.queryParams
      .subscribe(params => {
        console.log(params);

        if (params.retrying) {
          this.retrying = JSON.parse(params.retrying);
        }

        console.log(this.retrying); // price
      }
      );
  }

  async ngAfterViewInit(): Promise<void> {
    //Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    //Add 'implements AfterViewInit' to the class.

    // * branch in skip_payment_success_page: start the booth as if the image was tapped.
    // * returns before the point collect gate — no modal on a page nobody sees.
    if (this.skipping) {
      const started = await this.startDslrAndUnlock();
      if (!started) {
        // Booth refused or errored — reveal the page so it can be tapped to retry.
        this.skipping = false;
      }
      return;
    }

    // * do not show if 1) use pin, 2) use promo code (free or any promo type) 3) free whitelist branch 4) branch in skip_point_collection
    const isUsePin = localStorage.getItem(StorageKey.IS_PIN_USE) === 'true';
    const isUsePromo = localStorage.getItem(StorageKey.PROMO_USE) === 'true';
    const isFreeWhitelistBranch = localStorage.getItem(StorageKey.IS_FREE_WHITELIST) === 'true';
    const skipList = this.commonService.getConfigSkipPointCollection();
    const isSkipPointCollection = skipList?.some(item => item.includes(localStorage.getItem(StorageKey.BRANCH_CODE))) ?? false;
    if (isSkipPointCollection) {
      return;
    }
    if (!isUsePin && !isUsePromo && !isFreeWhitelistBranch) { //do not use pin or promo, show point collect modal
      this.openPointModal();
    }
    else { // use pin or promo, check further if in production, do not show point collect modal
      if (!environment.production) { // in development, show point collect modal for testing
        if (!isFreeWhitelistBranch) { // if not free whitelist branch, show point collect modal
          this.openPointModal();
        }
      }
    }

  }

  async touchReady() {

    if (sessionStorage.getItem(StorageKey.USE_OMEGA_RANDOM) === 'true') {
      if (sessionStorage.getItem(StorageKey.USE_OMEGA_RANDOM_READY_CLICK) === 'false') {
        this.branchInterface.ready_background_image_url = OmegaRandom.READY;
        this.showReadyImage(OmegaRandom.READY);
        sessionStorage.setItem(StorageKey.USE_OMEGA_RANDOM_READY_CLICK, 'true');
        return
      }
    }


    if (!this.dataDslrService.checkDslrBoothConfig()) {
      this.router.navigate([AppRoute.CONFIG]);
      return;
    }

    await this.startDslrAndUnlock();
  }

  /**
   * The effect of touching the ready image: start the booth session, then hand off to the
   * unlock route that the kiosk shell watches. Shared by the manual touch and the
   * skip_payment_success_page auto-start, so the auto-start never needs a synthetic click.
   *
   * Returns false when the booth could not be started — the caller decides the fallback.
   */
  private async startDslrAndUnlock(): Promise<boolean> {

    // touchReady() had no guard, so an auto-start racing an impatient double-tap could
    // fire two concurrent start requests at the booth.
    if (this.dslrInFlight) {
      return false;
    }
    this.dslrInFlight = true;

    try {
      this.commonService.isLoading = true;
      const result = await this.dataDslrService.startDslrCountdown().toPromise();
      // The booth reports this when a session is already running — still a success for us.
      if (result.IsSuccessful ||
        result.ErrorMessage === 'The booth is not on the start screen to start a session') {
        this.router.navigate([AppRoute.PAYMENT_SUCCESS_UNLOCK]);
        return true;
      }
      this.showDslrBoothErrorPopup();
      return false;
    }
    catch (err) {
      this.showDslrBoothErrorPopup();
      return false;
    }
    finally {
      this.commonService.isLoading = false;
      this.dslrInFlight = false;
    }
  }

  ngOnDestroy(): void {
    this.clearReadyImageTimers();
  }

  /**
   * Starts showing `url`: a spinner until it has fully downloaded, the built-in screen
   * if it has not by the deadline. No url: the built-in screen straight away.
   */
  showReadyImage(url: string | null | undefined) {
    this.clearReadyImageTimers();
    this.readyImageUrl = url || null;
    this.readyImageAttempt = 0;
    this.readyImageLoaded = false;
    this.builtinShown = false;

    if (!this.readyImageUrl) {
      this.readyImageSrc = null;
      this.showBuiltin();
      return;
    }

    this.readyImageSrc = this.readyImageUrl;
    this.readyState = 'loading';
    this.readyImageDeadline = setTimeout(() => {
      this.readyImageDeadline = null;
      if (!this.readyImageLoaded) {
        // The download is left running: if it completes, the picture still fades in.
        console.warn(`ready image not loaded after ${READY_IMAGE_TIMEOUT_MS}ms - showing the built-in screen`, this.readyImageUrl);
        this.showBuiltin();
      }
    }, READY_IMAGE_TIMEOUT_MS);
  }

  /** The picture is complete - only now is it shown, over whatever was there. */
  onReadyImageLoad() {
    if (!this.readyImageSrc) {
      return;
    }
    this.readyImageLoaded = true;
    this.clearReadyImageTimers();
    this.readyState = 'picture';
  }

  onReadyImageError() {
    if (!this.readyImageUrl || this.readyImageLoaded) {
      return;
    }
    if (this.readyImageAttempt >= READY_IMAGE_RETRIES) {
      console.warn('ready image failed to load - showing the built-in screen', this.readyImageUrl);
      this.clearReadyImageTimers();
      this.readyImageSrc = null;
      this.showBuiltin();
      return;
    }
    this.readyImageAttempt++;
    const attempt = this.readyImageAttempt;
    const url = this.readyImageUrl;
    this.readyImageRetryTimer = setTimeout(() => {
      this.readyImageRetryTimer = null;
      this.readyImageSrc = withRetryParam(url, attempt);
    }, READY_IMAGE_RETRY_DELAY_MS);
  }

  private showBuiltin() {
    this.readyState = 'builtin';
    this.builtinShown = true;
  }

  private clearReadyImageTimers() {
    if (this.readyImageRetryTimer !== null) {
      clearTimeout(this.readyImageRetryTimer);
      this.readyImageRetryTimer = null;
    }
    if (this.readyImageDeadline !== null) {
      clearTimeout(this.readyImageDeadline);
      this.readyImageDeadline = null;
    }
  }

  showDslrBoothErrorPopup() {
    Swal.fire({
      title: InterfaceErrorText.PAYMENT_SUCCESS_DSLR_ERROR_TITLE,
      text: InterfaceErrorText.PAYMENT_SUCCESS_DSLR_ERROR_TEXT,
    })
  }

  openPointModal() {
    // const configResult = this.commonService.getConfigUsePointCollection();
    // if (configResult) {
    // if (configResult.length > 0) {
    // if (configResult.includes(localStorage.getItem(StorageKey.BRANCH_CODE))) {
    let options: NgbModalOptions = {
      ...PHOTOBOOTH_MODAL_OPTIONS,
      windowClass: 'point-collect-modal',
    }
    this.modalRef = this.ngbModal.open(this.pointCollectModal, options);
    // }
    // }
    // }
  }

  handleCloseDialog() {
    this.modalRef.close();
  }

}

/**
 * The same URL with a throwaway query parameter, so a retry is a real request rather
 * than the browser handing back the copy (or the failure) it already has.
 */
export function withRetryParam(url: string, attempt: number): string {
  try {
    const u = new URL(url, window.location.href);
    u.searchParams.set('_retry', String(attempt));
    return u.toString();
  }
  catch {
    return url;
  }
}
